/**
 * Cloudinary URL optimizer.
 * Inserts delivery transformations into a Cloudinary URL so images are served
 * as WebP/AVIF when supported (f_auto), auto-compressed (q_auto) and, when a
 * width is given, downscaled to fit (c_limit never upscales).
 *
 *   cld(url)                       -> f_auto,q_auto
 *   cld(url, { w: 800 })           -> f_auto,q_auto,w_800,c_limit
 *   cld(url, { w: 800, q: 'auto:best' })
 *
 * Non-Cloudinary URLs (or already-transformed ones) are returned untouched.
 */
export function cld(url, { w, q = 'auto' } = {}) {
  if (typeof url !== 'string') return url;
  if (!url.includes('res.cloudinary.com') || !url.includes('/upload/')) return url;
  // Avoid double-transforming a URL that already has a transformation segment.
  if (/\/upload\/[^/]*(f_auto|q_auto|w_\d)/.test(url)) return url;
  const t = ['f_auto', `q_${q}`];
  if (w) t.push(`w_${w}`, 'c_limit');
  return url.replace('/upload/', `/upload/${t.join(',')}/`);
}

/**
 * Builds a `srcset` string so high-DPR screens get a sharp image instead of an
 * upscaled one. Pass the same widths you'd consider for the layout:
 *
 *   cldSrcSet(url, [500, 1000, 1500]) -> ".../w_500... 500w, .../w_1000... 1000w, ..."
 */
export function cldSrcSet(url, widths, { q = 'auto' } = {}) {
  if (typeof url !== 'string' || !Array.isArray(widths)) return undefined;
  if (!url.includes('res.cloudinary.com') || !url.includes('/upload/')) return undefined;
  return widths.map((w) => `${cld(url, { w, q })} ${w}w`).join(', ');
}
